import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { isUiMockMode } from "@/config/features";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import type { DocumentListKind, GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import { filterCreditNotes } from "@/domain/creditNotes/filterCreditNotes";
import { validateInvoiceFilterValues, serializeInvoiceFiltersToSearchParams } from "@/domain/invoices/invoiceFilterState";
import { DownloadListWorkspace } from "@/features/invoices/components/DownloadListWorkspace";
import { ExportModal } from "@/features/invoices/components/ExportModal";
import { CreditNotesTable } from "@/features/invoices/components/CreditNotesTable";
import { InvoiceCursorPager } from "@/features/invoices/components/InvoiceCursorPager";
import { InvoiceFiltersPanel } from "@/features/invoices/components/InvoiceFiltersPanel";
import { InvoiceTableToolbar } from "@/features/invoices/components/InvoiceTableToolbar";
import { StatChipInline } from "@/features/invoices/components/StatChipInline";
import { useCreditNoteSearch } from "@/features/invoices/hooks/useCreditNoteSearch";
import { useCustomers } from "@/features/invoices/hooks/useCustomers";
import { useInvoiceFilters } from "@/features/invoices/hooks/useInvoiceFilters";
import { useRowSelection } from "@/features/invoices/hooks/useRowSelection";
import { useVisitedPageItems } from "@/features/invoices/hooks/useVisitedPageItems";
import { PageHeader } from "@/features/layout/components/PageHeader";
import type { CreditNoteRow } from "@/types/api";
import { downloadCreditNotePdfDirect } from "@/lib/http/downloadDocument";
import { ApiErrorNotice } from "@/features/billing/components/ApiErrorNotice";
import { useFilterSearchButton } from "@/lib/query/useFilterSearchButton";
import { cn } from "@/utils/cn";

const EMPTY_DEFAULT_STATUSES: string[] = [];

function isExportableCn(row: CreditNoteRow): boolean {
  return row.exportable !== false && row.status === "issued";
}

const CREDIT_NOTE_LIST: DocumentListKind = "credit_note";

export function CreditNotesWorkspace() {
  const navigate = useNavigate();
  const { withGate } = usePlanAccess();
  const [billingGate, setBillingGate] = useState<{
    reason: BillingGateReason;
    capability: GatedCapability;
  } | null>(null);

  const openGate = (reason: BillingGateReason, capability: GatedCapability) => {
    setBillingGate({ reason, capability });
  };

  const filters = useInvoiceFilters(EMPTY_DEFAULT_STATUSES);
  const applied = filters.applied;
  const mock = isUiMockMode();

  const customersQuery = useCustomers();
  const customers = customersQuery.data?.items ?? [];

  const { data, isLoading, isFetching, isPlaceholderData, error } =
    useCreditNoteSearch({
    customerIds: applied.customerIds,
    statuses: applied.statuses,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    amountMin: applied.amountMin,
    amountMax: applied.amountMax,
    pageSize: filters.pageSize,
    after: filters.after,
  });

  const rawItems = data?.items ?? [];
  const { searchLoading, markSearchStarted } = useFilterSearchButton(
    filters.appliedSearchParams.toString(),
    isLoading,
    isFetching,
    isPlaceholderData,
  );

  const filteredByStripe = rawItems;

  const items = useMemo(
    () =>
      filterCreditNotes(filteredByStripe, { tableSearch: filters.tableSearch }),
    [filteredByStripe, filters.tableSearch],
  );

  const selection = useRowSelection(items, (row) => row.id, isExportableCn);
  const [allPagesSelected, setAllPagesSelected] = useState(false);

  const visitedResetKey = `${filters.appliedSearchParams}|${filters.pageSize}`;
  const { allVisited } = useVisitedPageItems(
    filteredByStripe,
    (row) => row.id,
    visitedResetKey,
    isExportableCn,
  );

  const loadedSelectableIds = useMemo(() => {
    const rows = filters.tableSearch.trim()
      ? filterCreditNotes(allVisited, { tableSearch: filters.tableSearch })
      : allVisited;
    return rows.filter(isExportableCn).map((r) => r.id);
  }, [allVisited, filters.tableSearch]);

  const pageSelectableCount = useMemo(
    () => items.filter(isExportableCn).length,
    [items],
  );

  const clearSelection = () => {
    selection.clear();
    setAllPagesSelected(false);
  };

  const exportableSelectedCount = selection.selectedCount;

  const [modalOpen, setModalOpen] = useState(false);

  const handleSearch = () => {
    markSearchStarted(
      serializeInvoiceFiltersToSearchParams(
        filters.draft,
        new URLSearchParams(),
      ).toString(),
    );
    filters.commitSearch(filters.draft);
    clearSelection();
  };

  const selectAllLoadedPages = () => {
    if (loadedSelectableIds.length === 0) return;
    selection.setSelectedIds(loadedSelectableIds);
    setAllPagesSelected(true);
  };

  const [rowDownloadError, setRowDownloadError] = useState<unknown>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const onRowDownload = (id: string, creditNoteNumber?: string | null) => {
    if (mock) {
      selection.setSelectedIds([id]);
      setModalOpen(true);
      return;
    }
    const reason = withGate("inline_pdf", () => {
      setRowDownloadError(null);
      setDownloadingId(id);
      void downloadCreditNotePdfDirect(id, { creditNoteNumber })
        .catch((e) => setRowDownloadError(e))
        .finally(() => setDownloadingId(null));
    });
    if (reason) openGate(reason, "inline_pdf");
  };

  const appliedFilterInvalid =
    validateInvoiceFilterValues(applied, {
      createdDateLabel: "CN created",
      includeDueDate: false,
    }).length > 0;
  const exportDisabled =
    filters.isDirty || appliedFilterInvalid || exportableSelectedCount === 0;
  const allPagesSameAsThisPage =
    loadedSelectableIds.length > 0 &&
    loadedSelectableIds.length <= pageSelectableCount &&
    filters.page === 1;
  const allPagesTitle = allPagesSameAsThisPage
    ? "Open more pages with Next to include additional rows"
    : "All rows from pages you've opened this search";

  const handleToggleRow = (id: string) => {
    setAllPagesSelected(false);
    selection.toggle(id);
  };

  const handleToggleAllOnPage = () => {
    selection.toggleAllOnPage();
    setAllPagesSelected(false);
  };

  const exportZipButton = (
    <button
      type="button"
      className="btn-main btn-export-zip shrink-0 text-sm"
      disabled={exportDisabled}
      onClick={() => {
        const reason = withGate(
          "selection_export",
          () => setModalOpen(true),
          CREDIT_NOTE_LIST,
        );
        if (reason) openGate(reason, "selection_export");
      }}
    >
      Export {exportableSelectedCount} credit note
      {exportableSelectedCount === 1 ? "" : "s"} as ZIP
    </button>
  );

  return (
    <>
      <DownloadListWorkspace
        pinned={
          <>
            <PageHeader
              title="Download Credit Notes"
              breadcrumbSuffix="Download Credit Notes"
              className="page-head-row--compact"
            />

            {rowDownloadError ? (
              <ApiErrorNotice
                error={rowDownloadError}
                context="download"
                className="mx-0 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              />
            ) : null}

            <InvoiceFiltersPanel
              draft={filters.draft}
              applied={applied}
              isDirty={filters.isDirty}
              customers={customers}
              customersLoading={customersQuery.isLoading}
              customersError={null}
              onDraftChange={filters.setDraft}
              onSearch={handleSearch}
              searchLoading={searchLoading}
              onReloadCustomers={() => void customersQuery.refetch()}
              showDueDate={false}
              layoutVariant="credit-notes"
              createdDateLabel="CN created"
              statusLabel="CN status"
              compact
            />

            <div className="table-meta-band table-meta-band--compact">
              <div className="selection-stats-inline">
                <StatChipInline n={filteredByStripe.length} label="on page" />
                <StatChipInline n={exportableSelectedCount} label="selected" />
              </div>

              <InvoiceTableToolbar
                pageSize={filters.pageSize}
                search={filters.tableSearch}
                findPlaceholder="CN # or invoice number…"
                findTitle="Client-side — current page only. Matches credit note # or linked invoice number."
                onPageSizeChange={(size) => {
                  filters.setPageSize(size);
                  clearSelection();
                }}
                onSearchChange={filters.setTableSearch}
                selection={{
                  pageSelectableCount,
                  allOnPageSelected: selection.allOnPageSelected,
                  someOnPageSelected: selection.someOnPageSelected,
                  onTogglePage: handleToggleAllOnPage,
                  onSelectAllPages: selectAllLoadedPages,
                  allPagesDisabled: allPagesSameAsThisPage || isLoading,
                  allPagesActive: allPagesSelected,
                  loadedPagesSelectableCount: loadedSelectableIds.length,
                  totalSelectedCount: exportableSelectedCount,
                  onClearSelection: clearSelection,
                  allPagesTitle,
                }}
              />
            </div>
          </>
        }
        table={
          <div className="panel invoices-workspace">
        {isLoading && rawItems.length === 0 ? (
          <p className="px-4 py-6 text-sm text-[var(--muted)]">Refreshing…</p>
        ) : null}
        {!error && rawItems.length > 0 ? (
          <CreditNotesTable
            items={items}
            customers={customers}
            selected={selection.selected}
            onToggle={handleToggleRow}
            onRowDownload={onRowDownload}
            downloadingId={downloadingId}
            embedded
            fixedColumnLayout
          />
        ) : null}
        {!isLoading && !isFetching && !error && rawItems.length === 0 ? (
          <p className="empty-state px-4 py-8 text-sm text-[var(--muted)]">
            No credit notes match these filters.
          </p>
        ) : null}
          </div>
        }
        pager={
          <InvoiceCursorPager
            compact
            itemCount={rawItems.length}
            page={filters.page}
            canGoPrev={filters.canGoPrev}
            hasMore={data?.has_more ?? false}
            pageLoading={isFetching && isPlaceholderData}
            onPrev={filters.goPrev}
            onNext={() => {
              const reason = withGate(
                "paginate",
                () => {
                  if (data?.next_cursor) filters.goNext(data.next_cursor);
                },
                CREDIT_NOTE_LIST,
              );
              if (reason) openGate(reason, "paginate");
            }}
            detail={
              filters.tableSearch && items.length !== rawItems.length
                ? `(${items.length} after find in table)`
                : filters.tableSearch
                  ? "(find in table active)"
                  : undefined
            }
          />
        }
        footer={
          <div
            className={cn(
              "export-sticky export-sticky--compact",
              filters.isDirty && "is-pending-search",
            )}
          >
            {exportZipButton}
            {filters.isDirty ? (
              <span className="text-sm font-semibold text-[var(--warn)]">
                Search to apply filter changes before selecting or exporting.
              </span>
            ) : null}
          </div>
        }
      />

      <ExportModal
        open={modalOpen}
        invoiceIds={selection.selectedIds}
        documentType="credit_note"
        title="Export credit note PDFs"
        onClose={() => setModalOpen(false)}
        onStarted={(jobId) => navigate(ROUTES.exportDetail(jobId))}
      />

      <BillingGateModal
        open={billingGate != null}
        reason={billingGate?.reason ?? null}
        capability={billingGate?.capability}
        onClose={() => setBillingGate(null)}
      />
    </>
  );
}
